<template>
  <div class="page-content usuarios-page">
    <!-- Solo superadmin / admin -->
    <div v-if="canAccess" class="usuarios-wrapper">
      <div class="usuarios-card">
        <!-- Header -->
        <div class="usuarios-header">
          <div>
            <p class="usuarios-title">Usuarios</p>
            <p class="usuarios-subtitle">
              Gestión de usuarios del sistema.
            </p>
          </div>

          <div class="usuarios-header-actions">
            <!-- Buscador -->
            <div class="usuarios-search">
              <input
                v-model="searchTerm"
                type="text"
                class="usuarios-search__input"
                placeholder="Buscar por nombre o correo…"
              />
            </div>

            <button
              type="button"
              class="ios-primary-button usuarios-add-btn"
              @click="openCreateModal"
            >
              + Agregar usuario
            </button>
          </div>
        </div>

        <!-- Lista -->
        <div class="usuarios-list-container">
          <div v-if="loading" class="usuarios-loading">
            Cargando usuarios…
          </div>

          <div
            v-else-if="paginatedUsers.length === 0"
            class="usuarios-empty"
          >
            No se encontraron usuarios.
          </div>

          <div v-else class="usuarios-list">
            <UserListItem
              v-for="u in paginatedUsers"
              :key="u.id_token"
              :user="u"
              :canEdit="canEditTarget(u)"
              @edit="openEditModal"
            />
          </div>
        </div>

        <!-- Paginación -->
        <div v-if="totalPages > 1" class="usuarios-pagination">
          <button
            type="button"
            class="usuarios-page-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            Anterior
          </button>

          <button
            v-for="page in pages"
            :key="page"
            type="button"
            class="usuarios-page-btn"
            :class="{ 'is-active': page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button
            type="button"
            class="usuarios-page-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <!-- Vista prohibida -->
    <VistaProhibida v-else />

    <!-- Modal -->
    <UserFormModal
      :visible="showFormModal"
      :mode="formMode"
      :user="selectedUser"
      :currentUserRole="userRole"
      :availableRoles="availableRolesForCurrentUser"
      @close="closeFormModal"
      @saved="handleFormSaved"
    />
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import VistaProhibida from '@/views/VistaProhibida.vue';
import UserListItem from './UserListItem.vue';
import UserFormModal from './UserFormModal.vue';
import { fetchUsers, fetchUserByToken } from './usuarios.service';

export default {
  name: 'UsuariosPanel',
  components: {
    VistaProhibida,
    UserListItem,
    UserFormModal,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      groups: [],
      loading: true,
      strictMode: false,
      perPage: 100,
      currentPage: 1,
      showFormModal: false,
      formMode: 'create', // 'create' | 'edit'
      selectedUser: null,
      searchTerm: '', // 🔎 nuevo
    };
  },
  computed: {
    userRole() {
      return this.$store.state.role || null;
    },
    userToken() {
      return this.$store.state.user?.token || null;
    },
    canAccess() {
      // Solo superadministrador o administrador
      return (
        this.userRole === 'superadministrador' ||
        this.userRole === 'administrador'
      );
    },
    // Aplana grupos -> lista de usuarios con info de grupo
    flattenedUsers() {
      const result = [];
      if (!Array.isArray(this.groups)) return result;

      this.groups.forEach((group) => {
        if (!group.users) return;
        group.users.forEach((u) => {
          result.push({
            ...u,
            group_name: group.group_name,
            group_token: group.group_id,
          });
        });
      });

      return result;
    },
    // 🔎 Filtrado por nombre, email, username, rol, grupo
    filteredUsers() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.flattenedUsers;

      return this.flattenedUsers.filter((u) => {
        const parts = [
          u.nombre,
          u.email,
          u.username,
          u.role,
          u.group_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return parts.includes(term);
      });
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredUsers.length / this.perPage)
      );
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredUsers.slice(start, start + this.perPage);
    },
    pages() {
      return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    },
    availableRolesForCurrentUser() {
      const currentUserRole = this.userRole;

      const roleMap = {
        superadministrador: [
          { value: 'superadministrador', text: 'Super Administrador' },
          { value: 'administrador', text: 'Administrador' },
          { value: 'usuario', text: 'Usuario' },
          { value: 'repartidor', text: 'Repartidor' },
          { value: 'recolector', text: 'Recolector' },
        ],
        administrador: [
          { value: 'administrador', text: 'Administrador' },
          { value: 'usuario', text: 'Usuario' },
          { value: 'repartidor', text: 'Repartidor' },
          { value: 'recolector', text: 'Recolector' },
        ],
        usuario: [],
        repartidor: [],
        recolector: [],
      };

      return roleMap[currentUserRole] || [];
    },
  },
  created() {
    if (this.canAccess) {
      this.loadUsers();
    } else {
      this.loading = false;
    }
  },
  watch: {
    // cuando cambie el filtro, regresamos a la página 1
    searchTerm() {
      this.currentPage = 1;
    },
  },
  methods: {
    async loadUsers() {
      if (!this.userToken) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const data = await fetchUsers({
          authorizationToken: this.userToken,
          // strict: this.strictMode,
        });
        this.groups = Array.isArray(data) ? data : [];
        this.currentPage = 1;
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        this.toast?.error('No se pudieron cargar los usuarios.');
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    canEditTarget(user) {
      const currentRole = this.userRole;
      const targetRole = user.role || 'usuario';
      const isSelf = this.userToken === user.id_token;

      if (!currentRole) return false;

      // Superadmin puede editar a cualquiera
      if (currentRole === 'superadministrador') return true;

      // Admin:
      // - No puede editar a superadministradores
      // - Puede editar a cualquier otro rol (incluido él mismo)
      if (currentRole === 'administrador') {
        if (targetRole === 'superadministrador') {
          return false;
        }
        return true;
      }

      // Otros roles: solo se podrían editar a sí mismos (por si se reutiliza)
      return isSelf;
    },

    openCreateModal() {
      this.formMode = 'create';
      this.selectedUser = null;
      this.showFormModal = true;
    },
    async openEditModal(user) {
      try {
        const fullUser = await fetchUserByToken(user.id_token);
        this.formMode = 'edit';
        this.selectedUser = fullUser;
        this.showFormModal = true;
      } catch (error) {
        console.error('Error al cargar usuario para edición:', error);
        this.toast?.error('No se pudo cargar el usuario para editar.');
      }
    },
    closeFormModal() {
      this.showFormModal = false;
    },
    async handleFormSaved() {
      await this.loadUsers();
    },
  },
};
</script>

<style scoped>
.usuarios-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.usuarios-wrapper {
  width: 100%;
  max-width: 920px;
}

.usuarios-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.usuarios-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.usuarios-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.usuarios-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.usuarios-header-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

/* 🔎 buscador */
.usuarios-search {
  position: relative;
  min-width: 180px;
}

.usuarios-search__input {
  width: 100%;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  font-size: 0.78rem;
  outline: none;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.usuarios-search__input::placeholder {
  color: #64748b;
}

.usuarios-search__input:focus {
  border-color: rgba(129, 140, 248, 0.9);
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.5);
  background: rgba(15, 23, 42, 0.98);
}

.usuarios-add-btn {
  padding-inline: 0.9rem;
  font-size: 0.84rem;
}

.usuarios-list-container {
  border-radius: 1.25rem;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.92);
  padding: 0.7rem 0.7rem 0.6rem;
  min-height: 120px;
}

.usuarios-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.usuarios-loading,
.usuarios-empty {
  font-size: 0.85rem;
  color: #e5e7eb;
  padding: 0.4rem 0.25rem;
}

/* Paginación */
.usuarios-pagination {
  display: flex;
  justify-content: center;
  gap: 0.3rem;
  margin-top: 0.9rem;
}

.usuarios-page-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  font-size: 0.78rem;
  padding: 0.25rem 0.65rem;
  cursor: pointer;
  min-width: 32px;
  text-align: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.1s ease;
}

.usuarios-page-btn:hover:not(:disabled) {
  background: rgba(15, 23, 42, 1);
  border-color: rgba(148, 163, 184, 0.95);
}

.usuarios-page-btn.is-active {
  background: #e5e7eb;
  color: #020617;
}

.usuarios-page-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Responsive */
@media (max-width: 768px) {
  .usuarios-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .usuarios-header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .usuarios-search {
    flex: 1;
    min-width: 0;
  }

  .usuarios-add-btn {
    white-space: nowrap;
  }
}
</style>
